import { useEffect, useRef, useState } from 'react';
import type { SearchResults, SearchType } from '@blackblack/shared';
import { formatCount } from '@blackblack/shared';
import { api } from '../../lib/api';
import { useQuery } from '../../lib/query';
import { goBack, Link, navigate, useSearchParams } from '../../lib/router';
import { Icon } from '../../components/ui/Icon';
import { Chips, EmptyState, ErrorState, Spinner } from '../../components/ui/primitives';
import { Page, Section, Shelf } from '../../components/layout';
import { UserRow } from '../../components/UserRow';
import { VideoGrid } from '../../components/VideoGrid';
import { Cover } from '../music/Cover';
import { TrackRow } from '../music/TrackRow';
import { PlaylistCard } from '../playlists/PlaylistCard';
import styles from './discover.module.css';

const TABS: Array<{ id: SearchType; label: string }> = [
  { id: 'all', label: 'Alles' },
  { id: 'users', label: 'Gebruikers' },
  { id: 'videos', label: "Video's" },
  { id: 'tracks', label: 'Muziek' },
  { id: 'artists', label: 'Artiesten' },
  { id: 'albums', label: 'Albums' },
  { id: 'playlists', label: 'Playlists' },
  { id: 'hashtags', label: 'Hashtags' },
];

const RECENT_KEY = 'bb:recent-searches';
function readRecent(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as string[];
  } catch {
    return [];
  }
}
function pushRecent(q: string) {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify([q, ...readRecent().filter((x) => x !== q)].slice(0, 8)));
  } catch {
    /* storage unavailable */
  }
}

/** Unified search with type filters; URL holds the state (?q=&type=) so it's shareable. */
export function SearchPage() {
  const params = useSearchParams();
  const q = params.get('q') ?? '';
  const type = (TABS.some((t) => t.id === params.get('type')) ? params.get('type') : 'all') as SearchType;
  const [input, setInput] = useState(q);
  const [recent, setRecent] = useState(readRecent);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!q) field.current?.focus();
  }, [q]);

  // Debounced search-as-you-type.
  useEffect(() => {
    const t = setTimeout(() => {
      if (input.trim() !== q) navigate(`/search?${new URLSearchParams({ q: input.trim(), type })}`, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  const res = useQuery<SearchResults>(q ? `search:${type}:${q}` : null, () => api.get('/search', { q, type, limit: 30 }), { staleMs: 15_000 });
  useEffect(() => {
    if (q && res.data) {
      pushRecent(q);
      setRecent(readRecent());
    }
  }, [q, res.data]);

  const setType = (t: SearchType) => navigate(`/search?${new URLSearchParams({ q, type: t })}`, { replace: true });
  const d = res.data;
  const nothing = d && Object.values(d).every((arr) => (arr as unknown[]).length === 0);

  return (
    <Page>
      <header className={styles.searchHeader}>
        <button className={styles.back} onClick={() => goBack('/discover')} aria-label="Terug">
          <Icon name="back" size={22} />
        </button>
        <form
          className={styles.searchField}
          onSubmit={(e) => {
            e.preventDefault();
            field.current?.blur();
            navigate(`/search?${new URLSearchParams({ q: input.trim(), type })}`, { replace: true });
          }}
          role="search"
        >
          <Icon name="search" size={18} />
          <input ref={field} value={input} onChange={(e) => setInput(e.target.value)} placeholder="Zoeken" aria-label="Zoeken" enterKeyHint="search" />
          {input && (
            <button type="button" onClick={() => setInput('')} aria-label="Wissen">
              <Icon name="close" size={16} />
            </button>
          )}
        </form>
      </header>
      <div className={styles.chipsWrap}>
        <Chips items={TABS} value={type} onChange={setType} />
      </div>

      {!q && (
        <div className={styles.recent}>
          {recent.length > 0 ? (
            <>
              <h2>Recent gezocht</h2>
              {recent.map((r) => (
                <button key={r} className={styles.recentItem} onClick={() => setInput(r)}>
                  <Icon name="search" size={16} /> {r}
                </button>
              ))}
            </>
          ) : (
            <EmptyState icon="search" title="Zoek op BlackBlack" text="Vind creators, video's, nummers, artiesten, albums, playlists en hashtags." />
          )}
        </div>
      )}

      {q && res.loading && (
        <div className={styles.center}>
          <Spinner />
        </div>
      )}
      {q && res.error && <ErrorState error={res.error} onRetry={res.refetch} />}
      {nothing && <EmptyState icon="search" title={`Geen resultaten voor "${q}"`} text="Probeer een ander woord of een ander filter." />}

      {d && (
        <>
          {d.users.length > 0 && (
            <Section title="Gebruikers" action={type === 'all' && d.users.length > 3 ? <button className={styles.more} onClick={() => setType('users')}>Meer</button> : undefined}>
              {(type === 'all' ? d.users.slice(0, 3) : d.users).map((u) => (
                <UserRow key={u.id} user={u} following={u.isFollowing ?? false} />
              ))}
            </Section>
          )}
          {d.tracks.length > 0 && (
            <Section title="Muziek" action={type === 'all' && d.tracks.length > 4 ? <button className={styles.more} onClick={() => setType('tracks')}>Meer</button> : undefined}>
              <div className={styles.rows}>
                {(type === 'all' ? d.tracks.slice(0, 4) : d.tracks).map((t, i, arr) => (
                  <TrackRow key={t.id} track={t} list={arr} index={i} context={`Zoeken: ${q}`} />
                ))}
              </div>
            </Section>
          )}
          {d.artists.length > 0 && (
            <Section title="Artiesten">
              <Shelf>
                {d.artists.map((a) => (
                  <Link key={a.id} to={`/artist/${a.id}`} className={styles.artist}>
                    <Cover src={a.imageUrl} size={96} radius={48} />
                    <span>{a.name}</span>
                  </Link>
                ))}
              </Shelf>
            </Section>
          )}
          {d.albums.length > 0 && (
            <Section title="Albums">
              <Shelf>
                {d.albums.map((a) => (
                  <Link key={a.id} to={`/album/${a.id}`} className={styles.artist}>
                    <Cover src={a.coverUrl} size={120} radius={12} />
                    <span>{a.title}</span>
                    <small>{a.artist.name}</small>
                  </Link>
                ))}
              </Shelf>
            </Section>
          )}
          {d.playlists.length > 0 && (
            <Section title="Playlists">
              <Shelf>
                {d.playlists.map((p) => (
                  <PlaylistCard key={p.id} playlist={p} showOwner />
                ))}
              </Shelf>
            </Section>
          )}
          {d.hashtags.length > 0 && (
            <Section title="Hashtags">
              <div className={styles.tags}>
                {d.hashtags.map((h) => (
                  <Link key={h.tag} to={`/tag/${h.tag}`} className={styles.tag}>
                    <span className={styles.hash}>#</span>
                    {h.tag}
                    <small>{formatCount(h.videoCount)}</small>
                  </Link>
                ))}
              </div>
            </Section>
          )}
          {d.videos.length > 0 && (
            <Section title="Video's">
              <VideoGrid items={d.videos} loading={false} hasMore={false} loadingMore={false} onLoadMore={() => {}} />
            </Section>
          )}
        </>
      )}
    </Page>
  );
}
